import { useCallback, useEffect } from 'react';

import { MbscResource } from '@mobiscroll/react';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { formatFullName } from 'src/common/util/formatFullName';
import { naturalStringCompare } from 'src/common/util/naturalStringCompare';
import { getStorageValue } from 'src/common/util/useLocalStorage/getStorageValue';
import {
  AssignmentSorting,
  assignmentSortStorageKey,
} from 'src/features/dispatch/constants';
import { setAssignmentSorting } from 'src/features/dispatch/dispatcher.slice';

export function useDispatchAssignmentSort() {
  const dispatch = useAppDispatch();
  const assignmentSorting = useAppSelector(
    (state) => state.dispatcher.assignmentSorting
  );

  const setAssignmentSortingState = useCallback(
    (sorting: AssignmentSorting) => {
      dispatch(setAssignmentSorting(sorting));
    },
    [dispatch]
  );

  useEffect(() => {
    const assignmentSort = getStorageValue<AssignmentSorting>(
      assignmentSortStorageKey,
      AssignmentSorting.START_TIME
    );

    dispatch(setAssignmentSorting(assignmentSort));
  }, [dispatch]);

  const sortCalendarResources = useCallback(
    (resources: MbscResource[]) => {
      const comparators: Record<
        AssignmentSorting,
        (a: MbscResource, b: MbscResource) => number
      > = {
        [AssignmentSorting.START_TIME]: (a, b) =>
          a.assignment.start.localeCompare(b.assignment.start),
        [AssignmentSorting.END_TIME]: (a, b) =>
          a.assignment.end.localeCompare(b.assignment.end),
        [AssignmentSorting.DRIVER_NAME]: (a, b) =>
          formatFullName(a.assignment.driver).localeCompare(
            formatFullName(b.assignment.driver)
          ),
        [AssignmentSorting.VEHICLE_NAME]: (a, b) =>
          naturalStringCompare(
            a.assignment.vehicle.nickname,
            b.assignment.vehicle.nickname
          ),
      };

      return [...resources].sort(comparators[assignmentSorting]);
    },
    [assignmentSorting]
  );

  return {
    assignmentSorting,
    setAssignmentSortingState,
    sortCalendarResources,
  };
}
